import { useChainId, useSwitchChain } from 'wagmi';
import { Badge, ChevronDown, Popover, Spinner, useToast } from '@/components/ui';
import { cn } from '@/lib/cn';
import { supportedChains } from '@/wagmi';

/**
 * `Popover` owns its own open state and only folds away on a mousedown outside
 * its root, so a selection made inside the panel has to emit one. Re-dispatching
 * on `document` is enough: the handler sees a target its root does not contain.
 */
function dismissPopover() {
  document.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
}

/**
 * Short labels + a distinct dot per chain, so the trigger stays narrow. These are
 * deliberately abbreviated: "Base Sepolia" rendered a 138px trigger against
 * "Base"'s 85px and was half the header's 768px overflow. The panel list and the
 * trigger's screen-reader text both still use the chain's full name.
 */
const CHAIN_META: Record<number, { short: string; dot: string }> = {
  8453: { short: 'Base', dot: 'bg-info' },
  84532: { short: 'Sepolia', dot: 'bg-floor' },
  4663: { short: 'Robinhood', dot: 'bg-up' },
  46632: { short: 'RH Sepolia', dot: 'bg-warn' },
  31337: { short: 'Localhost', dot: 'bg-subtle' },
};

function meta(id: number, name: string) {
  return CHAIN_META[id] ?? { short: name, dot: 'bg-subtle' };
}

export function ChainSwitcher() {
  const chainId = useChainId();
  const { switchChain, isPending, variables } = useSwitchChain();
  const { push } = useToast();

  const active = supportedChains.find((c) => c.id === chainId) ?? supportedChains[0];
  const activeMeta = meta(active.id, active.name);

  const onSelect = (id: number) => {
    dismissPopover();
    if (id === chainId) return;
    switchChain(
      { chainId: id as (typeof supportedChains)[number]['id'] },
      {
        onError: (error) =>
          push({
            tone: 'error',
            title: 'Could not switch network',
            description: error.message,
          }),
      },
    );
  };

  return (
    <Popover
      align="end"
      trigger={
        /* Popover wraps `trigger` in its own <button>, so this must not be one. */
        <span className="flex h-9 items-center gap-1.5 rounded-md border border-line bg-elevated px-2 text-sm font-medium text-muted transition-colors duration-200 ease-swift hover:border-line-strong hover:text-fg">
          <span aria-hidden="true" className={cn('h-2 w-2 shrink-0 rounded-full', activeMeta.dot)} />
          <span aria-hidden="true" className="hidden max-w-[9rem] truncate sm:inline">
            {activeMeta.short}
          </span>
          <span className="sr-only">Network: {active.name}. Change network</span>
          <ChevronDown size={14} className="text-subtle" />
        </span>
      }
      className="w-60"
    >
      <div>
        <p className="px-2 py-1.5 text-3xs font-semibold uppercase tracking-[0.14em] text-subtle">
          Network
        </p>
        <ul role="list" className="space-y-0.5">
          {supportedChains.map((chain) => {
            const m = meta(chain.id, chain.name);
            const selected = chain.id === chainId;
            const pending = isPending && variables?.chainId === chain.id;
            return (
              <li key={chain.id}>
                <button
                  type="button"
                  onClick={() => onSelect(chain.id)}
                  aria-current={selected ? 'true' : undefined}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left text-sm transition-colors duration-200 ease-swift',
                    selected ? 'bg-elevated text-fg' : 'text-muted hover:bg-elevated hover:text-fg',
                  )}
                >
                  <span aria-hidden="true" className={cn('h-2 w-2 shrink-0 rounded-full', m.dot)} />
                  <span className="min-w-0 flex-1 truncate font-medium">{chain.name}</span>
                  {'testnet' in chain && chain.testnet ? (
                    <Badge variant="warn" size="sm">
                      Testnet
                    </Badge>
                  ) : null}
                  {pending ? (
                    <Spinner size={14} />
                  ) : selected ? (
                    <span aria-hidden="true" className="text-bull">
                      ✓
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </Popover>
  );
}

export default ChainSwitcher;
